import { Dispatch, useEffect } from 'react';
import { History } from 'history';
import { initialise as initialiseEventManager } from 'managers/eventManager/EventManager';
import { useGetProductData } from 'hooks/useGetProductData';
import { useFeatureToggleContext } from 'providers/featureToggleProvider/FeatureToggleProvider';
import { FEATURE_TOGGLE, getThemeByProductId, SUBSCRIPTION_TYPE } from '../../../constants';
import { SessionApply, SessionApplyAttributes, SessionPredicate, SessionPropertiesType } from '../sessionType';
import { hasMobileAppSession, updateMobileAppSession } from './mobileAppSession';
import { hasCheckoutSession, updateCheckoutSession } from './checkoutSession';
import { hasQuoteAppSession, updateQuoteAppSession } from './quoteSession';
import { errorSession } from './errorSession';

class SessionApplier {
  private initialised = false;

  private readonly history: History;

  private readonly setState: Dispatch<Partial<SessionPropertiesType>>;
  private readonly attributes: SessionApplyAttributes;

  constructor(
    history: History,
    setState: Dispatch<Partial<SessionPropertiesType>>,
    attributes: SessionApplyAttributes
  ) {
    this.history = history;
    this.setState = setState;
    this.attributes = attributes;
  }

  when(predicate: SessionPredicate, apply: SessionApply): SessionApplier {
    if (predicate(this.history)) {
      apply(this.history, this.setState, this.attributes);
      this.initialised = true;
    }
    return this;
  }

  otherwise(apply: SessionApply): void {
    if (!this.initialised) apply(this.history, this.setState, this.attributes);
  }
}
export const initSession = (
  history: History,
  setState: Dispatch<Partial<SessionPropertiesType>>,
  attributes: SessionApplyAttributes
): void => {
  const applier = new SessionApplier(history, setState, attributes);
  applier
    .when(hasQuoteAppSession, updateQuoteAppSession)
    .when(hasMobileAppSession, updateMobileAppSession)
    .when(hasCheckoutSession, updateCheckoutSession)
    .otherwise(errorSession);
};

const isAnnualPrice = (isAnnualRoute: boolean, recurringInterval: string) => {
  if (isAnnualRoute) return true;

  return recurringInterval === 'ANNUAL';
};

export const useUpdateProductDetailsState = (
  history: History,
  state: SessionPropertiesType,
  setState: Dispatch<Partial<SessionPropertiesType>>
): void => {
  const { isFeatureEnabled } = useFeatureToggleContext();
  const isQuoteApiUpliftEnabled = isFeatureEnabled(FEATURE_TOGGLE.QUOTE_API_UPLIFT);
  const isQuoteFlow = state.subscriptionType === SUBSCRIPTION_TYPE.QUOTE && isQuoteApiUpliftEnabled;
  const {
    productData, isError, isSuccess, isLoading
  } = useGetProductData({
    productId: state.productId,
    promoCode: state.pc,
    activationCode: state.activationCode,
    useLegacy: state.useLegacy,
    useAnnual: state.isAnnualRoute,
    enabled: !isQuoteFlow
  });
  useEffect(() => {
    if (isQuoteFlow) return;
    if (isLoading) return;
    if (isError) {
      setState({ isInitialised: true });
      history.push('/error');
    }
    if (isSuccess) {
      const key = state.subscriptionType === SUBSCRIPTION_TYPE.TRIAL ? 'trialDeal' : 'deal';
      const verifiedPromoCode = productData.items[0][key]?.promoCode ?? '';
      const theme = getThemeByProductId(productData.items[0].productId, state.addonIds);
      initialiseEventManager({ theme, subscriptionType: state.subscriptionType });
      setState({
        productId: productData.items[0].productId,
        theme,
        promoCode: verifiedPromoCode,
        isAnnualPrice: isAnnualPrice(state.isAnnualRoute, productData.items[0][key]?.recurringInterval ?? ''),
        isInitialised: true,
        ...(state.subscriptionType === SUBSCRIPTION_TYPE.QUOTE && { region: productData.region })
      });
    }
  }, [
    productData,
    isError,
    isSuccess,
    isLoading,
    state.isInitialised,
    state.subscriptionType,
    history.location.pathname,
    state.pc]);
};
